import { Link } from '@tanstack/react-router';

import { AlternadorTema } from '@/componentes/AlternadorTema';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import {
  ACOES_DO_SPRINT,
  MODULOS_DO_SISTEMA,
  moduloEstaConstruido,
  type ModuloDoSistema,
  type SituacaoDaAcao,
} from '@/modulos/acesso/sprint';

/**
 * MÓDULO 22 — A PRÉVIA VISUAL.
 *
 * ## O que o v6 pede, e o que esta tela faz em vez disso
 *
 * O documento pede uma "galeria de telas conceituais": as imagens geradas na
 * concepção, reunidas num lugar só, para mostrar ao cliente como o sistema
 * *poderia* ficar.
 *
 * Esta tela mostra **o sistema que existe**, e a troca é deliberada. As
 * imagens de `insumos/visualizacao/` são, nas palavras da própria skill
 * `style`, *inspiração e não norma* — e o manual já as corrige em dois pontos.
 * Uma galeria delas, hoje, mostraria ao cliente telas que o produto não tem
 * mais, e o pior erro que uma prévia pode cometer é prometer o que não existe.
 *
 * O v6 escreve o risco de não ter o módulo: *"a proposta ficar abstrata demais"*.
 * O remédio para abstração não é um desenho bonito — é a tela de verdade,
 * aberta, com dado dentro.
 *
 * ## Nada aqui é inventado
 *
 * Cada linha sai do inventário de `acesso/sprint.ts`, que é fato conferível
 * abrindo o sistema: os módulos que a navegação desenha, e as ações que dá
 * para fazer agora — **inclusive os botões que estão na tela e estão
 * desligados**. Módulo que não existe não ganha descrição inventada: ele diz
 * que não existe, e diz o ciclo quando o repositório registra o ciclo.
 *
 * ## Onde ela mora, e por que não está no menu
 *
 * Sem entrada no menu lateral, de propósito. O vocabulário de ícones do manual
 * (p.14) tem doze entradas e **nenhuma é "prévia" ou "galeria"** — e o
 * `CLAUDE.md` é explícito: conceito novo sem entrada no manual é achado, não
 * escolha livre. Criar um ícone é decisão de quem assina o manual.
 *
 * Até lá ela é alcançada por "Meu perfil", que é onde quem apresenta o sistema
 * já vai para ver o que cada papel enxerga.
 */
export function PaginaPrevia() {
  const construidos = MODULOS_DO_SISTEMA.filter(moduloEstaConstruido);
  const porVir = MODULOS_DO_SISTEMA.filter((m) => !moduloEstaConstruido(m));

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina flex flex-col gap-6">
        <CabecalhoPagina
          sobretitulo="Módulo 22"
          titulo="Prévia do sistema"
          subtitulo="O que o CT Frotas já faz hoje, tela por tela — para mostrar a quem precisa decidir"
          acoes={<AlternadorTema forma="texto" />}
        />

        {/* O "comparativo claro e noturno" que o v6 pede não é um par de
            imagens: é o próprio sistema trocando de tema no botão acima. */}
        <p className="txt-corpo max-w-prose text-tinta-secundaria">
          As telas abaixo são as de verdade, com o dado da sua empresa — não desenhos de
          proposta. O botão de tema no topo troca entre claro e noturno em todo o sistema,
          que é a comparação que o documento pede.
        </p>

        <section className="flex flex-col gap-3">
          <h2 className="txt-secao text-tinta">
            {`No ar agora · ${construidos.length} ${construidos.length === 1 ? 'módulo' : 'módulos'}`}
          </h2>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(min(320px,100%),1fr))]">
            {construidos.map((modulo) => (
              <CartaoDoModulo key={modulo.id} modulo={modulo} />
            ))}
          </div>
        </section>

        {porVir.length === 0 ? null : (
          <section className="flex flex-col gap-3">
            <h2 className="txt-secao text-tinta">{`Ainda não construídos · ${porVir.length}`}</h2>
            {/* A honestidade aqui é o ponto da tela: o que falta aparece, e
                aparece dizendo que falta. Esconder faria a prévia prometer um
                sistema que não existe — o erro que ela veio evitar. */}
            <ul className="flex flex-col gap-2">
              {porVir.map((modulo: ModuloDoSistema) => (
                <li key={modulo.id} className="cartao px-4 py-3">
                  <span className="txt-corpo text-tinta">{modulo.rotulo}</span>
                  <span className="txt-linha2 block text-tinta-secundaria">
                    {modulo.ciclo ?? 'O ciclo deste módulo está no guia de construção e não foi trazido para o repositório.'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function CartaoDoModulo({ modulo }: { readonly modulo: ModuloDoSistema }) {
  const acoes = ACOES_DO_SPRINT.filter((a) => a.modulo === modulo.id);
  const funcionam = acoes.filter((a) => a.situacao === 'funciona');
  /* AS TRES SITUACOES NAO SE MISTURAM, e o inventario registra por que: dizer
     "esta na tela e desligado" sobre um controle que nao existe manda quem le
     procurar um botao que nunca existiu. */
  const quantas = (qual: SituacaoDaAcao) =>
    acoes.filter((a) => (a.situacao as SituacaoDaAcao) === qual).length;
  const desligadas = quantas('desligada');
  const semBotao = quantas('nao-desenhada');

  return (
    <article className="cartao flex min-w-0 flex-col gap-2 p-4">
      <h3 className="txt-subtitulo text-tinta">{modulo.rotulo}</h3>

      {funcionam.length === 0 ? (
        <p className="txt-linha2 text-tinta-secundaria">
          A tela existe; o inventário ainda não lista o que ela faz.
        </p>
      ) : (
        <ul className="flex min-w-0 flex-col gap-1">
          {funcionam.slice(0, 4).map((acao) => (
            <li key={acao.id} className="txt-apoio min-w-0 text-tinta-secundaria">
              {acao.rotulo}
            </li>
          ))}
        </ul>
      )}

      {/* BOTÃO DESLIGADO NÃO É AÇÃO INVENTADA: ele está na tela, a pessoa o vê,
          e quem apresenta o sistema precisa saber antes de ser perguntado. */}
      {desligadas === 0 ? null : (
        <p className="txt-linha2 text-tinta-secundaria">
          {`${desligadas} ${desligadas === 1 ? 'ação está na tela e ainda não responde' : 'ações estão na tela e ainda não respondem'}.`}
        </p>
      )}
      {semBotao === 0 ? null : (
        <p className="txt-linha2 text-tinta-secundaria">
          {`${semBotao} ${semBotao === 1 ? 'ação ainda não tem controle na tela' : 'ações ainda não têm controle na tela'}.`}
        </p>
      )}

      {modulo.para === undefined ? null : (
        <Link to={modulo.para} search={{}} className="botao botao-terciario mt-auto self-start">
          {`Abrir ${modulo.rotulo}`}
        </Link>
      )}
    </article>
  );
}
